import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../contexts/StoreContext';
import { Location, SubLocation } from '../types/location';
import { createNewStore } from '../services/storeService';
import { CreateStorePayload } from '../types/store';
import { getAllLocations, getSubLocationsByLocation } from '../services/locationService';
import { getCategoriesByStore } from '../services/categoryService';
import { Category } from '../types/category';
import { ApiErrorResponse } from '../services/authService';
import styles from './CreateStorePage.module.css';
import layoutStyles from '../styles/layout.module.css';
import Header from '../components/layout/Header';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Style, Icon } from 'ol/style';
import Alert from '../components/common/Alert';

const CreateStorePage: React.FC = () => {
  const { currentUser } = useAuth();
  const { addStoreToList } = useStore();
  const navigate = useNavigate();
  
  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';

  const [storeName, setStoreName] = useState('');
  const [storeDescription, setStoreDescription] = useState('');
  const [email, setEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [latitude, setLatitude] = useState<string | number>('');
  const [longitude, setLongitude] = useState<string | number>('');
  const [selectedLocationId, setSelectedLocationId] = useState<number | ''>('');
  const [selectedSubLocationId, setSelectedSubLocationId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingLocations, setIsFetchingLocations] = useState(false);
  const [isFetchingSubLocations, setIsFetchingSubLocations] = useState(false);
  const [isFetchingCategories, setIsFetchingCategories] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [subLocations, setSubLocations] = useState<SubLocation[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<Map | null>(null);
  const markerFeatureRef = useRef<Feature | null>(null);
  const vectorSourceRef = useRef<VectorSource | null>(null);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error'; isVisible: boolean }>({
    message: '',
    type: 'success',
    isVisible: false
  });

  useEffect(() => {
    if (alert.isVisible) {
      const timer = setTimeout(() => {
        setAlert(prev => ({ ...prev, isVisible: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert.isVisible]);

  const showAlert = (message: string, type: 'success' | 'error') => {
    setAlert({ message, type, isVisible: true });
  };

  const hideAlert = () => {
    setAlert(prev => ({ ...prev, isVisible: false }));
  };

  useEffect(() => {
    const fetchCategories = async () => {
      setIsFetchingCategories(true);
      try {
        const fetchedCategories = await getCategoriesByStore(0);
        setCategories(fetchedCategories);
      } catch (err) {
        setError('Could not load categories. Please try again later.');
      } finally {
        setIsFetchingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchLocations = async () => {
      setIsFetchingLocations(true);
      try {
        const result = await getAllLocations();
        setLocations(result.items);
      } catch (err) {
        setError('Could not load primary locations. Please try again later.');
      } finally {
        setIsFetchingLocations(false);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocationId !== '') {
      const fetchSubLocations = async () => {
        setIsFetchingSubLocations(true);
        setSubLocations([]);
        setSelectedSubLocationId('');
        try {
          const fetchedSubLocations = await getSubLocationsByLocation(Number(selectedLocationId));
          setSubLocations(fetchedSubLocations);
        } catch (err) {
          setError('Could not load sub-locations for the selected primary location.');
        } finally {
          setIsFetchingSubLocations(false);
        }
      };
      fetchSubLocations();
    } else {
      setSubLocations([]);
      setSelectedSubLocationId('');
    }
  }, [selectedLocationId]);

  useEffect(() => {
    const currentMapRef = mapRef.current;
    if (!currentMapRef) return;
    let destroyed = false;
    vectorSourceRef.current = new VectorSource();
    const vectorLayer = new VectorLayer({
      source: vectorSourceRef.current,
      style: new Style({
        image: new Icon({ anchor: [0.5, 1], src: 'https://openlayers.org/en/latest/examples/data/icon.png' })
      })
    });
    mapInstanceRef.current = new Map({
      target: currentMapRef,
      layers: [
        new TileLayer({ source: new OSM() }),
        vectorLayer,
      ],
      view: new View({ center: fromLonLat([0, 0]), zoom: 2 }),
    });
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (destroyed) return;
          const userLocation = fromLonLat([position.coords.longitude, position.coords.latitude]);
          mapInstanceRef.current?.getView().setCenter(userLocation);
          mapInstanceRef.current?.getView().setZoom(13);
        },
        () => {}
      );
    }
    mapInstanceRef.current.on('click', (event) => {
      if (!mapInstanceRef.current) return;
      const coords = toLonLat(event.coordinate);
      setLongitude(coords[0].toFixed(6));
      setLatitude(coords[1].toFixed(6));
    });
    return () => {
      destroyed = true;
      const currentMap = mapInstanceRef.current;
      if (currentMap) {
        currentMap.setTarget(undefined);
        mapInstanceRef.current = null;
      }
      if (currentMapRef) currentMapRef.innerHTML = '';
      markerFeatureRef.current = null;
      vectorSourceRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (
      latitude !== '' && longitude !== '' &&
      mapInstanceRef.current && vectorSourceRef.current
    ) {
      const newCoords = fromLonLat([Number(longitude), Number(latitude)]);
      if (!markerFeatureRef.current) {
        markerFeatureRef.current = new Feature({ geometry: new Point(newCoords) });
        vectorSourceRef.current.addFeature(markerFeatureRef.current);
      } else {
        const geom = markerFeatureRef.current.getGeometry();
        if (geom && geom instanceof Point) geom.setCoordinates(newCoords);
      }
      mapInstanceRef.current.getView().setCenter(newCoords);
    }
  }, [latitude, longitude]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);

    if (!currentUser) {
      setError('User not authenticated. Please login again.');
      setIsLoading(false); return;
    }
    if (selectedLocationId === '') {
        setError('Please select a primary location.');
        setIsLoading(false); return;
    }
    if (selectedSubLocationId === '') {
        setError('Please select a sub-location.');
        setIsLoading(false); return;
    }
    if (selectedCategoryIds.length === 0) {
        setError('Please select at least one category.');
        setIsLoading(false); return;
    }

    let combinedLongLat: string | null = null;
    if (latitude !== '' && longitude !== '') {
      combinedLongLat = `${latitude},${longitude}`;
    }

    const payload: CreateStorePayload = {
      store_name: storeName,
      store_description: storeDescription,
      email: email,
      mobile_number: mobileNumber || null,
      image_url: imageUrl || null,
      long_lat: combinedLongLat,
      is_active: isAdmin ? true : false,
      sub_location_id: Number(selectedSubLocationId),
      user_id: currentUser.id,
      category_ids: selectedCategoryIds,
    };

    try {
      const response = await createNewStore(payload);
      if (isAdmin) {
        addStoreToList(response.store);
        showAlert('Store created successfully!', 'success');
        setTimeout(() => {
          navigate('/select-store');
        }, 1500);
      } else {
        showAlert('Store request submitted successfully! It will be reviewed by an administrator.', 'success');
        setTimeout(() => {
          navigate('/select-store');
        }, 2000);
      }
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      showAlert(apiError.message || (isAdmin ? 'Failed to create store.' : 'Failed to submit store request.'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={layoutStyles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <main className={styles.contentArea}>
          <Alert 
            message={alert.message}
            type={alert.type}
            isVisible={alert.isVisible}
            onClose={hideAlert}
          />
          <div className={styles.titleBar}>
            <h2 className={styles.title}>Create New Store</h2>
          </div>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="categories">Categories *</label>
              <div className={styles.categorySelector}>
                {isFetchingCategories ? (
                  <div className={styles.loadingMessage}>Loading categories...</div>
                ) : categories.length === 0 ? (
                  <div className={styles.emptyMessage}>No categories available</div>
                ) : (
                  <div className={styles.categoryCheckboxes}>
                    {categories.map((category) => (
                      <label key={category.id} className={styles.categoryCheckbox}>
                        <input
                          type="checkbox"
                          checked={selectedCategoryIds.includes(category.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCategoryIds([...selectedCategoryIds, category.id]);
                            } else {
                              setSelectedCategoryIds(selectedCategoryIds.filter(id => id !== category.id));
                            }
                          }}
                          disabled={isLoading || isFetchingCategories}
                        />
                        <span>{category.name || category.category_name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {selectedCategoryIds.length > 0 && (
                <div className={styles.selectedCount}>
                  {selectedCategoryIds.length} categor{selectedCategoryIds.length === 1 ? 'y' : 'ies'} selected
                </div>
              )}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="storeName">Store Name *</label>
              <input type="text" id="storeName" value={storeName} onChange={(e) => setStoreName(e.target.value)} required disabled={isLoading} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="storeDescription">Description *</label>
              <textarea id="storeDescription" value={storeDescription} onChange={(e) => setStoreDescription(e.target.value)} required disabled={isLoading} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email *</label>
              <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isLoading} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="mobileNumber">Mobile Number</label>
              <input type="tel" id="mobileNumber" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} disabled={isLoading} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="imageUrl">Image URL</label>
              <input type="url" id="imageUrl" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} disabled={isLoading} />
            </div>

            <div className={styles.formGroup}>
              <label>Store Location on Map (Click to select)</label>
              <div ref={mapRef} className={styles.mapContainer} style={{ height: '300px', width: '100%', marginBottom: '15px' }}></div>
            </div>

            {/* Latitude/Longitude Row */}
            <div className={styles.latLongRow}>
              <div style={{ flex: 1 }}>
                <label htmlFor="latitude" className={styles.latLongLabel}>Latitude</label>
                <input
                  type="number"
                  step="any"
                  id="latitude"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value === '' ? '' : parseFloat(e.target.value))}
                  placeholder="e.g., 40.7128"
                  disabled={isLoading}
                  className={styles.latLongInput}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="longitude" className={styles.latLongLabel}>Longitude</label>
                <input
                  type="number"
                  step="any"
                  id="longitude"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value === '' ? '' : parseFloat(e.target.value))}
                  placeholder="e.g., -74.0060"
                  disabled={isLoading}
                  className={styles.latLongInput}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="location">Primary Location *</label>
              <select
                id="location"
                value={selectedLocationId}
                onChange={(e) => setSelectedLocationId(e.target.value === '' ? '' : Number(e.target.value))}
                required
                disabled={isLoading || isFetchingLocations}
              >
                <option value="" disabled>{isFetchingLocations ? 'Loading locations...' : 'Select a primary location'}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.location_name || `Location ID: ${loc.id}`}
                  </option>
                ))}
              </select>
            </div>

            {selectedLocationId !== '' && (
              <div className={styles.formGroup}>
                <label htmlFor="subLocation">Sub-Location *</label>
                <select
                  id="subLocation"
                  value={selectedSubLocationId}
                  onChange={(e) => setSelectedSubLocationId(e.target.value === '' ? '' : Number(e.target.value))}
                  required
                  disabled={isLoading || isFetchingSubLocations || subLocations.length === 0}
                >
                  <option value="" disabled>
                    {isFetchingSubLocations ? 'Loading sub-locations...' :
                     subLocations.length === 0 && !isFetchingSubLocations ? 'No sub-locations available' : 'Select a sub-location'}
                  </option>
                  {subLocations.map((subLoc) => (
                    <option key={subLoc.id} value={subLoc.id}>
                      {subLoc.sub_location_name || `SubLocation ID: ${subLoc.id}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {error && <p className={styles.errorMessage}>{error}</p>}
            <button type="submit" className={styles.submitButton} disabled={isLoading || isFetchingLocations || isFetchingSubLocations || isFetchingCategories}>
              {isLoading 
                ? (isAdmin ? 'Creating Store...' : 'Submitting Request...') 
                : (isAdmin ? 'Create Store' : 'Request Store')}
            </button>
          </form>
        </main>
      </div>
    </div>
  );
};

export default CreateStorePage; 